import { useCallback, useEffect, useRef, useState } from 'react';
import { get, ApiRequestError } from './api';
import type { PaginatedResponse } from './types';

// ── useQuery ──
export function useQuery<T>(fetcher: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const fetcherRef = useRef(fetcher);
  const mountedRef = useRef(true);

  // Keep fetcherRef current without triggering re-renders
  fetcherRef.current = fetcher;

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    try {
      const result = await fetcherRef.current();
      if (mountedRef.current) setData(result);
    } catch (e) {
      if (!mountedRef.current) return;
      if (e instanceof ApiRequestError) {
        setError(e.message || 'An unexpected error occurred');
        setErrorStatus(e.status);
      } else {
        setError(e instanceof Error ? e.message : 'Unknown error');
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []); // stable — never rebuilds

  useEffect(() => {
    mountedRef.current = true;
    refetch();
    return () => { mountedRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps]); // re-fetch only when deps change

  return { data, loading, error, errorStatus, refetch };
}

// ── usePaginatedQuery ──
export function usePaginatedQuery<T>(
  basePath: string,
  params: Record<string, string | number | undefined> = {},
  perPage = 50,
) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const paramsRef = useRef(params);
  paramsRef.current = params;

  const fetchPage = useCallback(async (p: number) => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      qs.set('page', String(p));
      qs.set('per_page', String(perPage));
      const current = paramsRef.current;
      for (const [k, v] of Object.entries(current)) {
        if (v !== undefined && v !== '') qs.set(k, String(v));
      }
      const result = await get<PaginatedResponse<T>>(`${basePath}?${qs}`);
      setData(result.data);
      setTotal((result as any).pagination?.total_count ?? result.total ?? 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [basePath, perPage]);

  const paramsKey = JSON.stringify(params);
  useEffect(() => {
    fetchPage(page);
  }, [fetchPage, page, paramsKey]);

  const totalPages = Math.ceil(total / perPage) || 1;

  return {
    data,
    total,
    page,
    totalPages,
    loading,
    error,
    setPage,
    refetch: () => fetchPage(page),
  };
}

// ── useMutation ──
export function useMutation<TInput, TOutput = void>(
  mutationFn: (input: TInput) => Promise<TOutput>,
) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (input: TInput): Promise<TOutput> => {
    setLoading(true);
    setError(null);
    try {
      const result = await mutationFn(input);
      return result;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      setError(msg);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [mutationFn]);

  return { mutate, loading, error };
}

// ── useDebounce ──
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Animates a number from 0 to target with ease-out cubic.
 * Used by MetricCard and HeroMetric for count-up effect.
 */
export function useCountAnimation(target: number, duration = 800): number {
  const [value, setValue] = useState(0);
  const prevTarget = useRef(target);

  useEffect(() => {
    const from = prevTarget.current !== target ? 0 : value;
    prevTarget.current = target;
    if (target === 0) {
      setValue(0);
      return;
    }
    const start = performance.now();
    let raf: number;
    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(from + (target - from) * eased));
      if (progress < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration]);

  return value;
}
